import axios from "axios";
import { config } from '../../config/Config';

const jsonHeaders = (accessToken) => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`
});

export const getUserPermissions = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user-permissions/${obj.userId}`,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching user permissions:', error.response?.data || error.message);
        throw error;
    }
};

export const getUserPermissionsForGame = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            // Game-level overrides only, unless includeGeographies: then every geography's rows too.
            url: `${config.serverBaseUrl}/v1/web/user-permissions/${obj.userId}/game/${obj.gameId}${obj.includeGeographies ? '?includeGeographies=true' : ''}`,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching user permissions for game:', error.response?.data || error.message);
        throw error;
    }
};

// Overrides stored at exactly one geography (segment) of a game. Returns only that tier — game-scoped
// rows are not merged in; use getEffectivePermissions for the resolved view.
export const getUserPermissionsForGameAndSegment = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user-permissions/${obj.userId}/game/${obj.gameId}/segment/${obj.segmentId}`,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching user permissions for segment:', error.response?.data || error.message);
        throw error;
    }
};

// The geographies of a game the user can be given overrides in (besides the whole game): [{ id, name }].
export const getOverrideGeographies = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user-permissions/${encodeURIComponent(obj.userId)}/game/${obj.gameId}/override-geographies`,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data?.geographies || [];
    } catch (error) {
        console.error('Error fetching override geographies:', error.response?.data || error.message);
        throw error;
    }
};

export const createOrUpdateUserPermissions = async (obj) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/user-permissions/overrides`,
            headers: jsonHeaders(obj.accessToken),
            data: obj.data
        });
        return response.data;
    } catch (error) {
        console.error('Error creating/updating user permissions:', error.response?.data || error.message);
        throw error;
    }
};

export const getEffectivePermissions = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user-permissions/effective`,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching effective permissions:', error.response?.data || error.message);
        throw error;
    }
};

// Remove every override a user holds at one geography of a game.
export const deleteUserPermissionOverridesForSegment = async (obj) => {
    try {
        const response = await axios({
            method: 'delete',
            url: `${config.serverBaseUrl}/v1/web/user-permissions/user/${obj.userId}/game/${obj.gameId}/segment/${obj.segmentId}`,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting segment permission overrides:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Scope-query override deletion. Scope is org → game → segment; omitted params are not sent.
 * Omitting segmentId deletes every segment's rows for that game as well as the game-level ones; pass
 * wholeGameOnly (with a gameId and no segmentId) to delete only the game-level ("Whole game") rows.
 */
export const deletePermissionOverrides = async (obj) => {
    try {
        const params = new URLSearchParams();
        if (obj.gameId) params.append('gameId', obj.gameId);
        if (obj.segmentId) params.append('segmentId', obj.segmentId);
        if (obj.wholeGameOnly) params.append('wholeGameOnly', 'true');

        const queryString = params.toString();
        const url = `${config.serverBaseUrl}/v1/web/user-permissions/user/${obj.userId}/overrides${queryString ? '?' + queryString : ''}`;

        const response = await axios({
            method: 'delete',
            url,
            headers: jsonHeaders(obj.accessToken)
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting permission overrides:', error.response?.data || error.message);
        throw error;
    }
};
